import React from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import FacetTitle from '../FacetTitle';

const NestedFacetList = ({
	facetGroups,
	onSelectFacet,
	hasAppliedFilters = false,
	onResetAll,
	headingRef
}) => {
	const labels = useLabels();
	const geoLocationLabel = labels.get('GEO_LOCATION');
	const filtersHeading = labels.get('FILTERS');
	const resetLabel = labels.get('RESET');
	const showReset = hasAppliedFilters && typeof onResetAll === 'function';

	return (
		<>
			{/* Desktop-only wrapper: composes a conditional pt-1 (2px) with the
			    heading's pt-2 (4px) to land ~6px when filters are applied, matching
			    the panel — pt-3 (8px) sat too low and there's no 6px spacing token. */}
			<div
				className={`d-none d-lg-block${
					hasAppliedFilters ? ' pt-1' : ''
				}`}
			>
				<div
					className={`nested-facet-heading d-flex align-items-center justify-content-between pb-5 px-0 ${
						hasAppliedFilters ? 'pt-2' : 'pt-4'
					}`}
				>
					<h2
						ref={headingRef}
						tabIndex="-1"
						className="nested-facet-heading-text d-flex align-items-center m-0 ddc-font-size-base"
					>
						<i
							className="ddc-icon ddc-icon-filter-list-controls mr-2"
							aria-hidden="true"
						/>
						<span>{filtersHeading}</span>
					</h2>
					{showReset && (
						<button
							type="button"
							className="btn btn-unstyled text-link-muted pr-0 pl-3 py-3"
							onClick={onResetAll}
						>
							{resetLabel}
						</button>
					)}
				</div>
			</div>
			<ul className="list-unstyled mb-0">
				{facetGroups.map((facetGroup, index) => {
					if (facetGroup.isTopLevel) {
						const nextGroup = facetGroups[index + 1];
						const isLastTopLevel =
							!nextGroup || !nextGroup.isTopLevel;
						const titleId = `nested-top-level-title-${facetGroup.facet.id}`;
						return (
							<li
								key={`nested-geo-${facetGroup.facet.id}`}
								className={`nested-facet-top-level-item py-3 px-0${
									isLastTopLevel ? ' border-bottom' : ''
								}`}
							>
								<div role="group" aria-labelledby={titleId}>
									<span id={titleId}>
										<FacetTitle
											facetTitle={
												facetGroup.facet.type === 'GEO'
													? geoLocationLabel
													: facetGroup.facet.label
											}
											groupId={facetGroup.facet.id}
										/>
									</span>
									{facetGroup.renderedContent}
								</div>
							</li>
						);
					}

					// Multi-group items carry groupId/groupTitleLabel directly
					// (matching the props accordion passes to MultiFacetGroup);
					// single-facet items use the real facet object.
					const id = facetGroup.groupId || facetGroup.facet.id;
					const title = facetGroup.groupTitleLabel
						? labels.get(facetGroup.groupTitleLabel)
						: facetGroup.facet.label;

					return (
						<li
							key={`nested-${id}`}
							className="nested-facet-list-item"
						>
							<button
								type="button"
								className="btn btn-unstyled w-100 d-flex justify-content-between align-items-center py-4 px-0 border-bottom text-left"
								onClick={() => onSelectFacet(facetGroup)}
								aria-label={`${title}, ${labels.get(
									'FILTER_OPTIONS'
								)}`}
								data-facet-id={id}
							>
								<FacetTitle facetTitle={title} groupId={id} />
								<i
									className="ddc-icon ddc-icon-skinny-arrow-right"
									aria-hidden="true"
								/>
							</button>
						</li>
					);
				})}
			</ul>
		</>
	);
};

NestedFacetList.propTypes = {
	facetGroups: PropTypes.arrayOf(
		PropTypes.shape({
			facet: PropTypes.shape({
				id: PropTypes.string.isRequired,
				label: PropTypes.string
			}),
			groupId: PropTypes.string,
			groupTitleLabel: PropTypes.string,
			isTopLevel: PropTypes.bool,
			renderedContent: PropTypes.node
		})
	).isRequired,
	onSelectFacet: PropTypes.func.isRequired,
	hasAppliedFilters: PropTypes.bool,
	onResetAll: PropTypes.func,
	headingRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

export default NestedFacetList;
